import React from 'react';
import { Link } from 'react-router-dom';
import { LayoutGridIcon, TagIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export type TagDisplayMode = 'cards' | 'compact';
export interface DisplayTag {
  id: string;
  name: string;
  description?: string;
  count?: number;
  category?: string;
  /** When present the tag renders as a Link; otherwise as a filter button. */
  href?: string;
}

/**
 * Canonical tag category palette. Previously duplicated as a local
 * `getTagColor` / `getTropeColor` switch in every content page — now defined
 * once so a category looks identical everywhere it appears.
 */
const CATEGORY_COLORS: Record<string, {
  light: string;
  dark: string;
}> = {
  Tropes: {
    light: 'bg-indigo-100 text-indigo-900',
    dark: 'bg-indigo-900/30 text-indigo-300'
  },
  Characters: {
    light: 'bg-orange-100 text-orange-900',
    dark: 'bg-orange-900/30 text-orange-300'
  },
  'Relationship Types': {
    light: 'bg-pink-100 text-pink-900',
    dark: 'bg-pink-900/30 text-pink-300'
  },
  Representation: {
    light: 'bg-purple-100 text-purple-900',
    dark: 'bg-purple-900/30 text-purple-300'
  },
  Themes: {
    light: 'bg-blue-100 text-blue-900',
    dark: 'bg-blue-900/30 text-blue-300'
  },
  Settings: {
    light: 'bg-green-100 text-green-900',
    dark: 'bg-green-900/30 text-green-300'
  },
  'Content Warnings': {
    light: 'bg-amber-100 text-amber-900',
    dark: 'bg-amber-900/30 text-amber-300'
  }
};
export const useTagCategoryColor = () => {
  const dk = useDarkClasses();
  return (category?: string): string => {
    const entry = category ? CATEGORY_COLORS[category] : undefined;
    if (!entry) return dk.isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-900';
    return dk.isDark ? entry.dark : entry.light;
  };
};
interface TagViewToggleProps {
  mode: TagDisplayMode;
  setMode: (mode: TagDisplayMode) => void;
  className?: string;
}

/** Cards | Compact switch. Mirrors the platform's existing view-toggle style. */
export const TagViewToggle: React.FC<TagViewToggleProps> = ({
  mode,
  setMode,
  className = ''
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  return <div role="group" aria-label="Tag display mode" className={`flex border rounded-lg overflow-hidden flex-shrink-0 ${dk.isDark ? 'border-gray-700' : 'border-gray-300'} ${className}`} data-id="element-1284" data-mp="83nk3vfhili8">
      <button onClick={() => setMode('cards')} aria-pressed={mode === 'cards'} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${mode === 'cards' ? `${rt.bgMedium} ${rt.textPrimary}` : `${dk.surface} ${dk.textSecondary}`}`} data-id="element-1285" data-mp="10s1kghu1ptwy">
        <LayoutGridIcon className="h-4 w-4" data-id="element-1286" data-mp="1jh872fxyn5o1" />
        Cards
      </button>
      <button onClick={() => setMode('compact')} aria-pressed={mode === 'compact'} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${mode === 'compact' ? `${rt.bgMedium} ${rt.textPrimary}` : `${dk.surface} ${dk.textSecondary}`}`} data-id="element-1287" data-mp="1jijcek720lh9">
        <TagIcon className="h-4 w-4" data-id="element-1288" data-mp="1vz2ga7ht3m10" />
        Compact
      </button>
    </div>;
};
interface TagListProps {
  tags: DisplayTag[];
  mode: TagDisplayMode;
  /** Show the category name inside each card (used on mixed-category pages). */
  showCategoryOnCard?: boolean;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  /** Overrides tag.count, e.g. counts recalculated for the current context. */
  countFor?: (tag: DisplayTag) => number | undefined;
  /**
   * Overrides the shared category palette. Content pages with their own
   * category vocabulary (Themes, Settings, Representation…) pass their existing
   * colour function so their established palette is preserved.
   */
  colorClassFor?: (tag: DisplayTag) => string;
}

/**
 * Renders a set of tags as either information cards or a dense pill grid.
 * Cards keep the established `rounded-lg p-3` layout; Compact is a rounded-full
 * pill grid with a 36px minimum height so it stays touch-friendly on mobile.
 */
export const TagList: React.FC<TagListProps> = ({
  tags,
  mode,
  showCategoryOnCard = false,
  selectedIds = [],
  onToggle,
  countFor,
  colorClassFor
}) => {
  const defaultColorFor = useTagCategoryColor();
  const colorFor = (tag: DisplayTag) => colorClassFor ? colorClassFor(tag) : defaultColorFor(tag.category);
  const resolveCount = (tag: DisplayTag) => countFor ? countFor(tag) : tag.count;
  if (mode === 'compact') {
    return <div className="flex flex-wrap gap-2" data-id="element-1289" data-mp="3ghuullhik42p">
        {tags.map(tag => {
        const isSelected = selectedIds.includes(tag.id);
        const count = resolveCount(tag);
        const inner = <>
              {tag.name}
              {typeof count === 'number' && <span className="text-xs opacity-75" data-id="element-1290" data-mp="qqg3jphupykc">{count}</span>}
              {isSelected && <XIcon className="h-3.5 w-3.5" data-id="element-1291" data-mp="z6f39p9m4v8g" />}
            </>;
        const classes = `${colorFor(tag)} inline-flex items-center gap-1.5 rounded-full pl-3 pr-2 py-1.5 text-sm font-medium min-h-[36px] transition-shadow hover:shadow-sm ${isSelected ? 'ring-2 ring-offset-1 ring-current' : ''}`;
        return tag.href ? <Link key={tag.id} to={tag.href} title={tag.description} className={classes} data-id="element-1292" data-mp="1hxy5htvtc59f">
              {inner}
            </Link> : <button key={tag.id} onClick={() => onToggle?.(tag.id)} aria-pressed={isSelected} title={tag.description} className={classes} data-id="element-1293" data-mp="189g6ni73g88f">
              {inner}
            </button>;
      })}
      </div>;
  }
  return <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-1294" data-mp="25ou3x9urxypn">
      {tags.map(tag => {
      const isSelected = selectedIds.includes(tag.id);
      const count = resolveCount(tag);
      const inner = <>
            <div className="flex justify-between items-center mb-1 gap-2" data-id="element-1295" data-mp="25ll8mcwlcnxv">
              <div className="font-medium" data-id="element-1296" data-mp="25npn1os495ce">{tag.name}</div>
              {typeof count === 'number' && <div className="text-xs opacity-80 whitespace-nowrap" data-id="element-1297" data-mp="21mmapc9a1rfx">
                  {count} books
                </div>}
            </div>
            {tag.description && <p className="text-xs mt-1 opacity-90" data-id="element-1298" data-mp="1rsn9cfh12bp1">{tag.description}</p>}
            {showCategoryOnCard && tag.category && <div className="mt-2" data-id="element-1299" data-mp="1hx21w1t6wj7w">
                <span className="text-xs font-medium opacity-75" data-id="element-1300" data-mp="1hy3nh07u0o3v">
                  {tag.category}
                </span>
              </div>}
          </>;
      const classes = `${colorFor(tag)} block text-left rounded-lg p-3 hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-offset-1 ring-current shadow-md' : ''}`;
      return tag.href ? <Link key={tag.id} to={tag.href} className={classes} data-id="element-1301" data-mp="3pg7l11sf0164">
            {inner}
          </Link> : <button key={tag.id} onClick={() => onToggle?.(tag.id)} aria-pressed={isSelected} className={classes} data-id="element-1302" data-mp="3tht454vjlbor">
            {inner}
          </button>;
    })}
    </div>;
};

/** Groups tags by category, preserving the order categories first appear. */
export const groupTagsByCategory = <T extends {
  category?: string;
},>(tags: T[]): {
  category: string;
  items: T[];
}[] => {
  const order: string[] = [];
  const buckets: Record<string, T[]> = {};
  for (const tag of tags) {
    const key = tag.category || 'Other';
    if (!buckets[key]) {
      buckets[key] = [];
      order.push(key);
    }
    buckets[key].push(tag);
  }
  return order.map(category => ({
    category,
    items: buckets[category]
  }));
};